import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  LayoutDashboard, Package, ShoppingCart, Users, FileText, CalendarClock,
  GraduationCap, Crown, Heart, Zap, Share2, Settings, BarChart3,
  Store, LogOut, Menu, X, CreditCard, Globe, Bell, Truck, Sparkles,
  Palette, UserCog, DollarSign, Layout, Tag, Send, PieChart, HelpCircle, Sliders, TrendingUp,
  Inbox, History, Activity, MessageSquare, Key, Clock, Rocket, Warehouse, Monitor,
  Webhook, BookOpen, Target, Boxes, Gift, Landmark, ClipboardCheck, Award, Video, Calendar, CalendarCheck,
  LayoutGrid, BookCopy, FileStack, SlidersHorizontal, ChevronDown, UserCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import MobileBottomNav from '@/components/MobileBottomNav';

// Mobile bottom-nav items for tenant owner — kept short (5 max) and focused on
// the most-used daily flows: dashboard, products, orders, customers, menu drawer.
const TENANT_BOTTOM_NAV = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Home', end: true },
  { to: '/dashboard/products', icon: Package, label: 'Produk' },
  { to: '/dashboard/orders', icon: ShoppingCart, label: 'Pesanan' },
  { to: '/dashboard/customers', icon: Users, label: 'Pelanggan' },
  { to: '/dashboard/settings', icon: Settings, label: 'Menu' },
];

const ACCOUNTING_ROLES = ['tenant', 'tenant_owner', 'tenant_admin', 'tenant_finance'];

const accountingReportLinks = [
  { to: '/dashboard/accounting/reports/trial-balance', label: 'Neraca saldo' },
  { to: '/dashboard/accounting/reports/pl', label: 'Laba rugi' },
  { to: '/dashboard/accounting/reports/balance-sheet', label: 'Neraca' },
  { to: '/dashboard/accounting/reports/cash-flow', label: 'Arus kas' },
  { to: '/dashboard/accounting/reports/journal-register', label: 'Daftar jurnal' },
  { to: '/dashboard/accounting/reports/ledger', label: 'Buku besar' },
  { to: '/dashboard/accounting/reports/compare', label: 'Bandingkan periode' },
];

function AccountingNavSection({ userRole, closeMobile }) {
  const loc = useLocation();
  const [reportsOpen, setReportsOpen] = useState(() => loc.pathname.includes('/accounting/reports'));

  useEffect(() => {
    if (loc.pathname.includes('/accounting/reports')) setReportsOpen(true);
  }, [loc.pathname]);

  if (!ACCOUNTING_ROLES.includes(userRole)) return null;

  const mainCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
      isActive ? 'bg-[#2D6CDF]/10 text-[#2D6CDF]' : 'text-slate-600 hover:bg-slate-50'
    }`;
  const subCls = ({ isActive }) =>
    `flex items-center gap-2 py-1.5 pl-3 pr-3 rounded-md text-xs font-medium transition-all ${
      isActive ? 'bg-[#2D6CDF]/10 text-[#2D6CDF]' : 'text-slate-500 hover:bg-slate-50'
    }`;

  const onReports = loc.pathname.includes('/accounting/reports');

  return (
    <div className="mb-4">
      <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">Akuntansi</p>
      <div className="space-y-0.5">
        <NavLink to="/dashboard/accounting" end onClick={closeMobile} className={mainCls}>
          <LayoutGrid size={17} />
          Ringkasan
        </NavLink>
        <NavLink to="/dashboard/accounting/coa" onClick={closeMobile} className={mainCls}>
          <BookCopy size={17} />
          Bagan akun
        </NavLink>
        <NavLink to="/dashboard/accounting/journals" onClick={closeMobile} className={mainCls}>
          <FileStack size={17} />
          Jurnal
        </NavLink>
        <NavLink to="/dashboard/accounting/settings" onClick={closeMobile} className={mainCls}>
          <SlidersHorizontal size={17} />
          Pengaturan
        </NavLink>
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setReportsOpen((o) => !o)}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
              onReports ? 'bg-[#2D6CDF]/10 text-[#2D6CDF]' : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <BookOpen size={17} className="shrink-0" />
            <span className="flex-1">Laporan</span>
            <ChevronDown size={16} className={`shrink-0 transition-transform ${reportsOpen ? 'rotate-180' : ''}`} />
          </button>
          {reportsOpen && (
            <div className="mt-1 space-y-0.5 border-l-2 border-[#2D6CDF]/25 pl-2 ml-3">
              {accountingReportLinks.map(({ to, label }) => (
                <NavLink key={to} to={to} onClick={closeMobile} className={subCls}>
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const navSections = [
  {
    title: 'Utama',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
      { to: '/dashboard/analytics', icon: PieChart, label: 'Analytics' },
      { to: '/dashboard/products', icon: Package, label: 'Produk' },
      { to: '/dashboard/inventory', icon: Warehouse, label: 'Inventori' },
      { to: '/dashboard/serial-keys', icon: Key, label: 'Serial Key', feature: 'serial_key' },
      { to: '/dashboard/orders', icon: ShoppingCart, label: 'Pesanan' },
      { to: '/dashboard/customers', icon: Users, label: 'Pelanggan' },
      { to: '/dashboard/support-tickets', icon: MessageSquare, label: 'Tiket Support' },
    ]
  },
  {
    title: 'POS / Kasir',
    items: [
      { to: '/dashboard/pos', icon: Monitor, label: 'Kasir POS', feature: 'pos' },
      { to: '/dashboard/pos-shifts', icon: Clock, label: 'Riwayat Shift', feature: 'pos' },
      { to: '/dashboard/pos-reports', icon: BarChart3, label: 'Laporan POS', feature: 'pos' },
      { to: '/dashboard/pos-settings', icon: Settings, label: 'Pengaturan POS', feature: 'pos' },
    ]
  },
  {
    title: 'Keuangan',
    items: [
      { to: '/dashboard/invoices', icon: FileText, label: 'Invoice' },
      { to: '/dashboard/payment-plans', icon: CalendarClock, label: 'Payment Plan', feature: 'payment_plan' },
      { to: '/dashboard/bank-accounts', icon: Landmark, label: 'Rekening Bank' },
      { to: '/dashboard/payment-confirmations', icon: ClipboardCheck, label: 'Konfirmasi Transfer' },
      { to: '/dashboard/cod', icon: Truck, label: 'COD' },
      { to: '/dashboard/high-ticket', icon: DollarSign, label: 'High-Ticket', feature: 'high_ticket' },
      { to: '/dashboard/accounting', icon: Landmark, label: 'Akuntansi', roles: ['tenant', 'tenant_owner', 'tenant_admin', 'tenant_finance'], feature: 'accounting' },
      { to: '/dashboard/accounting/reports', icon: BookOpen, label: 'Laporan akuntansi', roles: ['tenant', 'tenant_owner', 'tenant_admin', 'tenant_finance'], feature: 'accounting' },
    ]
  },
  {
    title: 'Modul',
    items: [
      { to: '/dashboard/courses', icon: GraduationCap, label: 'Kursus / LMS', feature: 'course' },
      { to: '/dashboard/certificates', icon: Award, label: 'Sertifikat', feature: 'course' },
      { to: '/dashboard/lms-enhanced', icon: GraduationCap, label: 'LMS Enhanced', feature: 'course' },
      { to: '/dashboard/memberships', icon: Crown, label: 'Membership', feature: 'membership' },
      { to: '/dashboard/donations', icon: Heart, label: 'Donasi', feature: 'donation' },
      { to: '/dashboard/webinars', icon: Video, label: 'Webinar', feature: 'webinar' },
      { to: '/dashboard/events', icon: Calendar, label: 'Event & Tiket', feature: 'event_ticket' },
      { to: '/dashboard/bookings', icon: CalendarCheck, label: 'Booking', feature: 'booking' },
      { to: '/dashboard/ppob', icon: Zap, label: 'PPOB', feature: 'ppob' },
      { to: '/dashboard/ppob-enhanced', icon: Zap, label: 'PPOB Enhanced', feature: 'ppob' },
      { to: '/dashboard/high-ticket-projects', icon: DollarSign, label: 'Projects', feature: 'high_ticket' },
    ]
  },
  {
    title: 'Marketing',
    items: [
      { to: '/dashboard/affiliates', icon: Share2, label: 'Affiliate', feature: 'affiliate' },
      { to: '/dashboard/resellers', icon: Store, label: 'Reseller', feature: 'reseller' },
      { to: '/dashboard/broadcast', icon: Send, label: 'Broadcast', feature: 'broadcast' },
      { to: '/dashboard/followup', icon: Clock, label: 'Follow-Up', feature: 'marketing_automation' },
      { to: '/dashboard/abandoned-carts', icon: ShoppingCart, label: 'Abandoned Cart', feature: 'marketing_automation' },
      { to: '/dashboard/followup-settings', icon: MessageSquare, label: 'Atur Follow-Up', feature: 'marketing_automation' },
      { to: '/dashboard/vouchers', icon: Tag, label: 'Voucher & Promo', feature: 'vouchers' },
      { to: '/dashboard/order-bumps', icon: Zap, label: 'Order Bumps', feature: 'upsell' },
      { to: '/dashboard/upsell-funnels', icon: TrendingUp, label: 'Upsell & Downsell', feature: 'upsell' },
      { to: '/dashboard/loyalty-settings', icon: Gift, label: 'Loyalty & Poin', feature: 'loyalty' },
      { to: '/dashboard/loyalty-rewards', icon: Gift, label: 'Reward Catalog', feature: 'loyalty' },
      { to: '/dashboard/bundles', icon: Boxes, label: 'Bundling', feature: 'bundles' },
      { to: '/dashboard/segments', icon: Users, label: 'Segmentasi', feature: 'segments' },
      { to: '/dashboard/landing-pages', icon: Rocket, label: 'LP Cekout', feature: 'landing_builder' },
      { to: '/dashboard/leads', icon: Target, label: 'Lead Management', feature: 'lead_capture' },
      { to: '/dashboard/lead-settings', icon: Sliders, label: 'Atur Lead', feature: 'lead_capture' },
    ]
  },
  {
    title: 'Store',
    items: [
      { to: '/dashboard/store-builder', icon: Store, label: 'Store Builder' },
      { to: '/dashboard/page-builder', icon: Layout, label: 'Page Builder', feature: 'page_builder' },
      { to: '/dashboard/templates', icon: Palette, label: 'Templates' },
      { to: '/dashboard/whitelabel', icon: Tag, label: 'Whitelabel', feature: 'white_label' },
      { to: '/dashboard/domains', icon: Globe, label: 'Domain', feature: 'custom_domain' },
      { to: '/dashboard/shipping', icon: Truck, label: 'Pengiriman' },
    ]
  },
  {
    title: 'CMS',
    items: [
      { to: '/dashboard/blog', icon: FileText, label: 'Blog', feature: 'blog' },
      { to: '/dashboard/faq', icon: HelpCircle, label: 'FAQ' },
      { to: '/dashboard/store-extras', icon: Sliders, label: 'Social & Widget' },
    ]
  },
  {
    title: 'Pengaturan',
    items: [
      { to: '/dashboard/notification-center', icon: Inbox, label: 'Pusat Notifikasi' },
      { to: '/dashboard/notification-logs', icon: History, label: 'Riwayat Notifikasi' },
      { to: '/dashboard/notifications', icon: Bell, label: 'Template Notifikasi' },
      { to: '/dashboard/settings', icon: Settings, label: 'Integrasi' },
      { to: '/dashboard/staff', icon: UserCog, label: 'Staff & Role', feature: 'multi_staff' },
      { to: '/dashboard/reports', icon: BarChart3, label: 'Laporan' },
      { to: '/dashboard/advanced-reports', icon: PieChart, label: 'Advanced Reports', feature: 'reports' },
      { to: '/dashboard/finance-reports', icon: TrendingUp, label: 'Finance Reports', feature: 'reports' },
      { to: '/dashboard/activity-logs', icon: Activity, label: 'Log Aktivitas' },
      { to: '/dashboard/subscription', icon: Sparkles, label: 'Subscription' },
      { to: '/dashboard/account', icon: UserCircle, label: 'Akun Saya' },
      { to: '/dashboard/saas-affiliate', icon: Share2, label: 'Affiliate SaaS' },
      { to: '/dashboard/import-export', icon: FileText, label: 'Import / Export', feature: 'import_export' },
      { to: '/dashboard/api-keys', icon: Key, label: 'API Keys', feature: 'api_access' },
      { to: '/dashboard/webhooks', icon: Webhook, label: 'Webhooks', feature: 'api_access' },
      { to: '/dashboard/api-docs', icon: BookOpen, label: 'API Docs', feature: 'api_access' },
      { to: '/dashboard/tutorial', icon: BookOpen, label: 'Tutorial' },
    ]
  },
];

export default function TenantLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isImpersonating = !!localStorage.getItem('admin_token');
  const navigate = useNavigate();

  // Tenant feature flags (drives which menu items are visible)
  const [tenantFeatures, setTenantFeatures] = useState(null); // null = loading, never gates

  useEffect(() => {
    api.get('/tenant/me/features')
      .then((res) => setTenantFeatures(res.data?.features || []))
      .catch(() => setTenantFeatures(null));
  }, []);

  // Notification bell state
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const bellRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/in-app-notifications/unread-count');
      setUnreadCount(data.count || 0);
    } catch (err) { /* silent */ }
  }, []);

  const fetchRecentNotifs = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/in-app-notifications', { params: { page: 1, limit: 5, is_read: 'false' } });
      setRecentNotifs(data.notifications || []);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (bellOpen) fetchRecentNotifs();
  }, [bellOpen, fetchRecentNotifs]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markNotifRead = async (id) => {
    try {
      await api.put(`/tenant/in-app-notifications/${id}/read`);
      setRecentNotifs(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* silent */ }
  };

  function timeAgo(dateStr) {
    const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
    if (diff < 60) return 'Baru saja';
    if (diff < 3600) return `${Math.floor(diff / 60)}m lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}j lalu`;
    return `${Math.floor(diff / 86400)}h lalu`;
  }

  const exitImpersonation = () => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin/tenants';
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50" data-testid="tenant-layout">
      {isImpersonating && (
        <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-between shrink-0">
          <span>⚠️ Mode Impersonasi — masuk sebagai <strong>{user?.email}</strong></span>
          <button onClick={exitImpersonation} className="underline font-semibold hover:opacity-80 ml-4">Kembali ke Admin</button>
        </div>
      )}
      <div className="flex flex-1 min-h-0" data-testid="tenant-inner">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-slate-200">
          <img src="/logo.png" alt="Tokomoe" className="h-8 object-contain" />
          <p className="text-xs text-slate-500 mt-1">Tenant Dashboard</p>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {navSections.map((section) => {
            if (section.type === 'accounting') {
              return <AccountingNavSection key="nav-accounting" userRole={user?.role} closeMobile={() => setSidebarOpen(false)} />;
            }
            return (
              <div key={section.title} className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 mb-2">{section.title}</p>
                {section.items
                  .filter((it) => !it.roles || it.roles.includes(user?.role))
                  .filter((it) => !it.feature || !tenantFeatures || tenantFeatures.includes(it.feature))
                  .map(({ to, icon: Icon, label, end }) => (
                  <NavLink key={to} to={to} end={end} onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-[#2D6CDF]/10 text-[#2D6CDF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Icon size={17} />
                    {label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <Link
            to="/dashboard/account"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-[#2D6CDF] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.[0] || 'T'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </Link>
          <Button variant="ghost" size="sm" className="w-full mt-2 text-slate-500 hover:text-red-600" onClick={logout} data-testid="tenant-logout-btn">
            <LogOut size={16} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="tenant-menu-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <h2 className="text-sm font-semibold text-slate-700">Tenant Panel</h2>
          <div className="ml-auto flex items-center gap-2">
            {/* Notification Bell */}
            <div className="relative" ref={bellRef}>
              <Button variant="ghost" size="icon" className="relative" onClick={() => setBellOpen(!bellOpen)}>
                <Bell size={20} className="text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
              {/* Dropdown */}
              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Notifikasi</h3>
                    {unreadCount > 0 && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                        {unreadCount} baru
                      </span>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {recentNotifs.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell size={24} className="text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-400">Tidak ada notifikasi baru</p>
                      </div>
                    ) : (
                      recentNotifs.map(notif => (
                        <div key={notif.id}
                          className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0"
                          onClick={() => { markNotifRead(notif.id); }}>
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-[#2D6CDF] mt-1.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{notif.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50">
                    <button onClick={() => { setBellOpen(false); navigate('/dashboard/notification-center'); }}
                      className="text-xs font-medium text-[#2D6CDF] hover:underline w-full text-center">
                      Lihat Semua Notifikasi →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-[1600px] mx-auto fade-in">
            <Outlet />
          </div>
        </main>
        <MobileBottomNav items={TENANT_BOTTOM_NAV} />
      </div>
    </div>
    </div>
  );
}
